import React, { useState, useEffect } from 'react';
import BaseModal from './BaseModal';
import Button from '../common/Button';
import { useContract } from '../../hooks/useContract';
import { formatPrice } from '../../utils/formatting';
import { convertEtherToWei } from '../../utils/web3';

const RenewLicenseModal = ({ 
  isOpen, 
  onClose, 
  nft, 
  account,
  onSuccess,
  onError 
}) => {
  const { 
    contract, 
    getRenewEnabled, 
    getRemainingLicenseTime, 
    getTier,
    getPublicPrices,
    renewLicense,
    getETCPOWBalance,
    approveETCPOW
  } = useContract(account);

  const [isLoading, setIsLoading] = useState(false);
  const [renewEnabled, setRenewEnabled] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [tier, setTier] = useState(1);
  const [renewalCost, setRenewalCost] = useState('0');
  const [balance, setBalance] = useState('0');
  const [hasApproval, setHasApproval] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('check'); // 'check', 'approve', 'renew', 'success'

  // Constants from contract
  const MAX_TOTAL_LICENSE = 365 * 24 * 60 * 60 + 180 * 24 * 60 * 60; // 18 months in seconds
  const RENEWAL_DURATION = 180 * 24 * 60 * 60; // 180 days in seconds

  useEffect(() => {
    if (isOpen && nft && contract) {
      loadRenewalInfo();
    }
  }, [isOpen, nft, contract]);

  const loadRenewalInfo = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Get renewal status
      const [enabled, time, tokenTier, prices, userBalance] = await Promise.all([
        getRenewEnabled(),
        getRemainingLicenseTime(nft.tokenId),
        getTier(nft.tokenId),
        getPublicPrices(),
        getETCPOWBalance()
      ]);

      setRenewEnabled(enabled);
      setRemainingTime(Number(time));
      setTier(tokenTier);
      setBalance(userBalance);

      // Calculate renewal cost (half of public mint price for the tier)
      const tierPrice = prices[tokenTier] || '0';
      const cost = (parseFloat(tierPrice) / 2).toString();
      setRenewalCost(cost);

      setStep('check');
    } catch (err) {
      console.error('Error loading renewal info:', err);
      setError('Failed to load renewal information');
    } finally {
      setIsLoading(false);
    }
  };

  const canRenew = () => {
    if (!renewEnabled) return false;
    if (remainingTime === 0) return false; // Expired
    if (remainingTime === Number.MAX_SAFE_INTEGER) return false; // Lifetime
    if (remainingTime >= MAX_TOTAL_LICENSE) return false; // Already maxed
    return true;
  };

  const getRenewalStatus = () => {
    if (!renewEnabled) {
      return { 
        status: 'disabled', 
        message: 'License renewals are currently disabled by the administrator.',
        color: 'text-gray-400'
      };
    }
    
    if (remainingTime === 0) {
      return { 
        status: 'expired', 
        message: 'This license has expired and cannot be renewed.',
        color: 'text-red-400'
      };
    }
    
    if (remainingTime === Number.MAX_SAFE_INTEGER) {
      return { 
        status: 'lifetime', 
        message: 'This is a lifetime license and does not need renewal.',
        color: 'text-green-400'
      };
    }
    
    if (remainingTime >= MAX_TOTAL_LICENSE) {
      return { 
        status: 'maxed', 
        message: 'This license has already reached the maximum duration of 18 months.',
        color: 'text-yellow-400'
      };
    }
    
    const newTotalTime = remainingTime + RENEWAL_DURATION;
    const actualRenewalDuration = newTotalTime > MAX_TOTAL_LICENSE 
      ? MAX_TOTAL_LICENSE - remainingTime 
      : RENEWAL_DURATION;
    
    return { 
      status: 'eligible', 
      message: `You can renew this license for ${Math.floor(actualRenewalDuration / (24 * 60 * 60))} days.`,
      color: 'text-green-400'
    };
  };

  const handleApprove = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Approve ETCPOW for renewal cost
      const costInWei = convertEtherToWei(renewalCost);
      await approveETCPOW(costInWei);
      
      setHasApproval(true);
      setStep('renew');
    } catch (err) {
      console.error('Approval error:', err);
      setError(err.message || 'Failed to approve ETCPOW');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenew = async () => {
    try {
      setIsLoading(true);
      setError('');

      const txHash = await renewLicense(nft.tokenId);
      
      setStep('success');
      if (onSuccess) {
        onSuccess(txHash, nft);
      }
      
      // Close modal after success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Renewal error:', err);
      setError(err.message || 'Failed to renew license');
      if (onError) {
        onError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renewalStatus = getRenewalStatus();
  const isEligible = renewalStatus.status === 'eligible';
  const hasEnoughBalance = parseFloat(balance) >= parseFloat(renewalCost);

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Renew License">
      <div className="space-y-6">
        {/* NFT Info */}
        <div className="bg-dark-surface/50 rounded-lg p-4 border border-dark-border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-text-primary">{nft.name}</h3>
            <span className="text-text-muted text-sm">#{nft.tokenId}</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-text-muted">Tier:</span>
              <span className="ml-2 text-text-primary font-medium">
                {tier === 2 ? 'Pro' : 'Normal'}
              </span>
            </div>
            <div>
              <span className="text-text-muted">Current Time:</span>
              <span className="ml-2 text-text-primary font-medium">
                {remainingTime === Number.MAX_SAFE_INTEGER ? 'Lifetime' : 
                 remainingTime === 0 ? 'Expired' :
                 `${Math.floor(remainingTime / (24 * 60 * 60))} days`}
              </span>
            </div>
          </div>
        </div>

        {/* Renewal Status */}
        <div className={`text-center p-4 rounded-lg border ${
          isEligible ? 'bg-green-500/10 border-green-500/30' : 
          'bg-red-500/10 border-red-500/30'
        }`}>
          <p className={renewalStatus.color}>{renewalStatus.message}</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Cost and Balance */}
        {isEligible && (
          <div className="bg-dark-surface/50 rounded-lg p-4 border border-dark-border">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-text-muted">Renewal Cost:</span>
                <span className="text-etc-primary font-semibold">{formatPrice(renewalCost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Your Balance:</span>
                <span className={`font-medium ${
                  hasEnoughBalance ? 'text-green-400' : 'text-red-400'
                }`}>
                  {formatPrice(balance)}
                </span>
              </div>
              {!hasEnoughBalance && (
                <p className="text-red-400 text-sm mt-2">
                  Insufficient ETCPOW balance for renewal
                </p>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1"
          >
            Cancel
          </Button>

          {isEligible && hasEnoughBalance && (
            <>
              {step === 'check' && (
                <Button
                  variant="primary"
                  onClick={handleApprove}
                  disabled={isLoading}
                  className="flex-1"
                >
                  {isLoading ? 'Approving...' : 'Approve & Renew'}
                </Button>
              )}

              {step === 'renew' && (
                <Button
                  variant="success"
                  onClick={handleRenew}
                  disabled={isLoading}
                  className="flex-1"
                >
                  {isLoading ? 'Renewing...' : 'Confirm Renewal'}
                </Button>
              )}

              {step === 'success' && (
                <Button
                  variant="success"
                  disabled
                  className="flex-1"
                >
                  ✅ Renewal Successful!
                </Button>
              )}
            </>
          )}
        </div>

        {/* Info Text */}
        {isEligible && (
          <div className="text-xs text-text-muted space-y-1">
            <p>• Renewal adds 180 days to your license (or less if near 18-month max)</p>
            <p>• Maximum license duration is 18 months from original mint</p>
            <p>• Renewal cost is 50% of the current public mint price</p>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default RenewLicenseModal;
